import { Button, Stack, TextField, Typography } from '@mui/material';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { authApi } from '@/pages/public/auth/auth-api';

export default function TwoFactorEnrollConfirm({
    onConfirmed,
    onSkip,
}: {
    onConfirmed: () => void;
    onSkip?: () => void;
}) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [codeError, setCodeError] = useState('');

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();

        const normalizedCode = code.replace(/\D/g, '').slice(0, 6);
        if (normalizedCode.length !== 6) {
            setCodeError(t('auth.two-factor-setup.confirm.error.invalid'));
            return;
        }

        setCodeError('');
        setLoading(true);
        try {
            const result = await authApi.verifyLegacyTotp({ code: normalizedCode });
            if (!result.valid) {
                setCodeError(t('auth.two-factor-setup.confirm.error.invalid'));
                return;
            }
            onConfirmed();
        } catch {
            enqueueSnackbar(t('auth.two-factor-setup.confirm.error.default'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Typography variant="h5" fontWeight={700}>
                    {t('auth.two-factor-setup.confirm.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('auth.two-factor-setup.confirm.subtitle')}
                </Typography>
            </Stack>

            <Stack component="form" spacing={2} onSubmit={onSubmit}>
                <TextField
                    label={t('auth.two-factor-setup.confirm.code-label')}
                    value={code}
                    onChange={(e) => {
                        setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                        if (codeError) setCodeError('');
                    }}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    fullWidth
                    error={!!codeError}
                    helperText={codeError || undefined}
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 6,
                    }}
                />

                <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
                    {loading ? t('auth.common.loading') : t('auth.two-factor-setup.confirm.submit')}
                </Button>

                {onSkip && (
                    <Button variant="text" size="small" onClick={onSkip} sx={{ alignSelf: 'center' }}>
                        {t('auth.two-factor-setup.confirm.skip')}
                    </Button>
                )}
            </Stack>
        </Stack>
    );
}
